'use es6';

import { isJsRenderedModule, moduleHasAnyJs } from './ModuleHelpers';
import * as moduleRenderClient from '../api/moduleRenderClient';
import * as RenderContext from '../constants/RenderContext';
import { isEmptyObject } from 'ContentUtils/helpers/ObjectHelpers';
const ISLAND_ATTRIBUTE_SELECTOR = '[data-hs-island="true"]';
function isJsRenderedModuleWithIsland(schema, renderedDomNode) {
  return isJsRenderedModule(schema) && Boolean(renderedDomNode.querySelector(ISLAND_ATTRIBUTE_SELECTOR));
}
export default class AppModuleMarkupRequestManager {
  constructor({
    onFormatModuleForRequest,
    onFormatRequestData,
    onHtmlReceivedForModule,
    onModuleHtmlRequestIsInvalid,
    onRenderError,
    shouldUseContentEditorRendering = false,
    allowCss = false,
    // When true, JS-bearing modules are fetched and routed through
    // onModuleHtmlRequestIsInvalid with `moduleHtml` + `assets` populated
    // so the consumer can mount the JS itself. Scoped to opt-in callers
    // only; existing consumers (including islands) see the legacy payload.
    allowJs = false
  }) {
    this.formatModuleForRequest = (module, smartRuleModuleBody) => {
      if (this.onFormatModuleForRequest) {
        return this.onFormatModuleForRequest(module);
      } else {
        const existingBody = !smartRuleModuleBody || isEmptyObject(smartRuleModuleBody) ? module.body : smartRuleModuleBody;
        const formattedModule = Object.assign({}, module, {
          body: Object.assign({}, existingBody || {}, {
            no_wrapper: false
          })
        });
        if (formattedModule.module_id && !formattedModule.body.module_id) {
          formattedModule.body.module_id = formattedModule.module_id;
        }
        return formattedModule;
      }
    };
    this.fetchHtmlForModule = (module, smartRuleModuleBody, query = {}) => {
      const moduleForRequest = this.formatModuleForRequest(module, smartRuleModuleBody);
      let data;
      if (this.onFormatRequestData) {
        data = this.onFormatRequestData(moduleForRequest);
      } else {
        data = moduleForRequest;
      }
      if (this.shouldUseContentEditorRendering) {
        return moduleRenderClient.renderModuleWithMeta(data, query);
      } else {
        return moduleRenderClient.renderWithMeta(data, query);
      }
    };
    this.requestModuleMarkup = ({
      module,
      moduleName,
      moduleVersion,
      options = {},
      schema,
      smartRuleModuleBody
    }) => {
      if (module && (!moduleHasAnyJs(schema) || this.allowJs)) {
        this.fetchHtmlForModule(module, smartRuleModuleBody, options.query).then(resp => {
          const {
            assets,
            html,
            renderedWithContext
          } = resp;
          const moduleHasJs = this.allowJs && moduleHasAnyJs(schema) || assets && !!(assets.jsHead.length || assets.jsFooter.length);
          const moduleHasCss = assets && !!assets.css.length;
          const hasRenderErrors = resp.errors && resp.errors.length > 0;
          const isFullRenderContextAndIgnoringAssets = renderedWithContext === RenderContext.FULL_CONTENT;
          const parsedModuleNode = this.domParser.parseFromString(html, 'text/html');
          const moduleHasIsland = isJsRenderedModuleWithIsland(schema, parsedModuleNode);
          const cssToAdd = assets ? [...assets.cssToAdd] : [];
          if (this.allowCss && !isFullRenderContextAndIgnoringAssets && assets) {
            cssToAdd.unshift(...assets.css);
          }
          if (isFullRenderContextAndIgnoringAssets && !hasRenderErrors || !moduleHasJs && (this.allowCss || !moduleHasCss) && !moduleHasIsland && !hasRenderErrors) {
            this.onHtmlReceivedForModule({
              cssToAdd,
              moduleHtml: html,
              moduleName,
              moduleVersion,
              options
            });
          } else {
            if (hasRenderErrors && this.onRenderError) {
              this.onRenderError(module, resp.errors);
            }
            // The api request succeeded, but we still can't use the html
            // JS modules (when allowJs is opted in) and islands intentionally
            // land here rather than the success path: the consumer needs the
            // assets to mount the JS, which onHtmlReceivedForModule can't do.
            this.onModuleHtmlRequestIsInvalid(Object.assign({
              cssToAdd,
              moduleHasCss,
              moduleHasIsland,
              moduleHasJs,
              moduleHtml: html
            }, this.allowJs && moduleHasJs ? {
              assets
            } : {}, {
              moduleName,
              moduleVersion,
              options
            }));
          }
        }).catch(() => {
          // The api request failed, so we do not have access to the html
          this.onModuleHtmlRequestIsInvalid({
            moduleName,
            moduleVersion,
            options
          });
        });
      } else {
        // We can't make use of this html when modules have custom js
        this.onModuleHtmlRequestIsInvalid({
          moduleHasJs: true,
          moduleName,
          moduleVersion,
          options
        });
      }
    };
    // Required config
    this.onModuleHtmlRequestIsInvalid = onModuleHtmlRequestIsInvalid;
    this.onHtmlReceivedForModule = onHtmlReceivedForModule;
    // Optional config
    this.onFormatModuleForRequest = onFormatModuleForRequest;
    this.onFormatRequestData = onFormatRequestData;
    this.onRenderError = onRenderError;
    this.allowCss = allowCss;
    this.allowJs = allowJs;
    this.shouldUseContentEditorRendering = shouldUseContentEditorRendering;
    this.domParser = new DOMParser();
  }
}